import React, { FC, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { ScrollView } from 'react-native';

import ConfirmDialog from 'components/confirm-dialog';
import Modal from 'components/modal';
import { OfferDisclaimer } from 'components/offer-disclaimer/offer-disclaimer';
import Picture from 'components/picture';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import noop from 'utils/noop';

import {
  BodyContent,
  CouponTitle,
  Description,
  Directions,
  ModalInner,
  Name,
  PictureHeading,
  RedeemInNext,
  ShortCode,
  ShortCodeWrapper,
  StyledCountdown,
  UseCodeAtCashier,
} from './styled';
import { IRedemptionModalProps } from './types';

const RedemptionModal: FC<React.PropsWithChildren<IRedemptionModalProps>> = ({
  redemptionStartTime = 0,
  selectedOffer,
  onDismiss = noop,
}) => {
  const [closeConfirmationIsOpen, setCloseConfirmationIsOpen] = useState(false);
  const handleModalDismiss = useCallback(() => {
    setCloseConfirmationIsOpen(true);
  }, []);
  const handleDialogDismiss = useCallback(() => {
    setCloseConfirmationIsOpen(false);
  }, []);
  const { formatMessage } = useIntl();

  return (
    <Modal
      onDismiss={handleModalDismiss}
      amplitudeEventData={{
        modalAppearanceEventMessage: 'Redeem Offer',
      }}
    >
      <ModalInner testID="redemption-modal">
        <ScrollView>
          <BodyContent>
            <PictureHeading>
              <Picture
                image={selectedOffer?.localizedImage?.locale?.app!}
                alt={selectedOffer?.localizedImage?.locale?.imageDescription! || ''}
                placeholderAspectRatio={400 / 170}
                objectFitContain
              />
            </PictureHeading>
            <CouponTitle>
              <Name content={selectedOffer?.name?.localeRaw} />
            </CouponTitle>
            <Description content={selectedOffer?.description?.localeRaw} />
            <UseCodeAtCashier>{formatMessage({ id: 'useCodeAtCashier' })}</UseCodeAtCashier>
            <ShortCodeWrapper>
              <ShortCode
                testID="redemption-short-code"
                aria-describedby="redemption-code-description"
              >
                {selectedOffer.shortCode || '5000'}
              </ShortCode>
              <VisuallyHidden
                testID="redemption-code-description"
                accessibilityLabel={formatMessage({ id: 'redemptionCode' })}
              />
            </ShortCodeWrapper>
            <Directions>
              <RedeemInNext>{formatMessage({ id: 'redeemInNext' })}</RedeemInNext>
              <StyledCountdown
                useClockStyle
                endTime={redemptionStartTime + 60 * 15 * 1000}
                onFinish={onDismiss}
              />
            </Directions>
            <OfferDisclaimer offer={selectedOffer} />
          </BodyContent>
        </ScrollView>
      </ModalInner>
      {closeConfirmationIsOpen && (
        <ConfirmDialog
          showDialog={closeConfirmationIsOpen}
          heading={formatMessage({ id: 'offerConfirmDialogHeading' })}
          body={formatMessage({ id: 'offerConfirmDialogBody' })}
          cancelLabel={formatMessage({ id: 'cancel' })}
          confirmLabel={formatMessage({ id: 'offerConfirmDialogConfirmLabel' })}
          onDismiss={handleDialogDismiss}
          onCancel={handleDialogDismiss}
          onConfirm={onDismiss}
          modalAppearanceEventMessage="Confirm Close Redemption"
        />
      )}
    </Modal>
  );
};

export default RedemptionModal;
